import { useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { useToast } from '../../lib/toast';
import { Banner, EmptyState, LoadingPage, Tag } from '../../components/ui';

interface Insight {
  id: string;
  category: string;
  severity: 'info' | 'watch' | 'concern';
  title: string;
  description: string;
  figures: Array<{ label: string; value: string }>;
  comparison: string;
  source: { report: string; reportId: string; area: string; page: number | null; transactionHint: string | null };
  impact: string | null;
  review: string;
  confidence: 'high' | 'medium' | 'low';
  caveat: string | null;
}

const SEVERITY: Record<Insight['severity'], { tone: 'info' | 'warn' | 'bad'; label: string }> = {
  info: { tone: 'info', label: 'For information' },
  watch: { tone: 'warn', label: 'Watch' },
  concern: { tone: 'bad', label: 'Worth a closer look' },
};

export function Insights() {
  const { fail } = useToast();
  const state = useAsync(() => api.finInsights(), []);
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState<Record<string, unknown> | null>(null);
  const [asking, setAsking] = useState(false);
  const [explained, setExplained] = useState<Record<string, { explanation: string; questions: string[]; ai: boolean }>>({});
  const data = state.data as {
    hasData: boolean;
    asOf?: string;
    insights?: Insight[];
    aiAvailable?: boolean;
    notice?: string;
    suggestedQuestions?: string[];
  } | null;

  if (!data) return state.loading ? <LoadingPage label="Loading insights…" /> : null;
  if (!data.hasData) {
    return <EmptyState title="No approved statement to observe yet." hint="Insights are calculated from approved figures only. Nothing is invented." />;
  }

  const insights = data.insights ?? [];

  async function ask(text: string) {
    setAsking(true);
    try {
      setAnswer(await api.finAsk(text));
      setQuestion(text);
    } catch (error) {
      fail(error, 'That question could not be answered.');
    } finally {
      setAsking(false);
    }
  }

  async function explain(id: string) {
    try {
      const result = await api.finInsightExplain(id);
      setExplained((cur) => ({ ...cur, [id]: result }));
    } catch (error) {
      fail(error, 'Could not explain that observation.');
    }
  }

  return (
    <div>
      <p className="mut">{data.notice} Through {data.asOf}.</p>
      {insights.length === 0 ? (
        <Banner kind="ok">Nothing unusual stands out from the approved statements and the current forecast.</Banner>
      ) : (
        insights.map((i) => {
          const sev = SEVERITY[i.severity];
          const extra = explained[i.id];
          return (
            <div className="card" key={i.id}>
              <div className="card-head">
                <h3 style={{ margin: 0 }}>{i.title}</h3>
                <div className="row" style={{ gap: 6 }}>
                  <Tag tone={sev.tone}>{sev.label}</Tag>
                  <Tag>{i.confidence} confidence</Tag>
                </div>
              </div>
              <p style={{ marginTop: 0 }}>{i.description}</p>
              <div className="stat-grid">
                {i.figures.map((f) => (
                  <div className="stat" key={f.label}><span className="value" style={{ fontSize: 18 }}>{f.value}</span><span className="label">{f.label}</span></div>
                ))}
              </div>
              <p className="mut">Compared with: {i.comparison}</p>
              <p className="mut">
                Source: {i.source.area} · {i.source.report}
                {i.source.page ? ` · page ${i.source.page}` : ''}
                {i.source.transactionHint ? ` · ${i.source.transactionHint}` : ''}
              </p>
              {i.impact && <p><strong>Possible impact.</strong> {i.impact}</p>}
              <p><strong>Suggested review.</strong> {i.review}</p>
              {i.caveat && <Banner kind="info">{i.caveat}</Banner>}
              <button type="button" className="btn s" onClick={() => void explain(i.id)}>
                {data.aiAvailable ? 'Explain in plain language' : 'Show questions to ask'}
              </button>
              {extra && (
                <div style={{ marginTop: 10 }}>
                  <p>{extra.explanation}</p>
                  <p className="mut">{extra.ai ? 'Wording from the AI, using only the figures above.' : 'Suggested questions, from the numbers themselves.'}</p>
                  <ul>{extra.questions.map((q) => <li key={q}>{q}</li>)}</ul>
                </div>
              )}
            </div>
          );
        })
      )}

      <div className="card">
        <div className="card-head"><h3>Ask HOA Financials</h3></div>
        <p className="mut">
          Answers are calculated from this community’s approved statements. The assistant will not invent a figure or decide what to spend.
        </p>
        <div className="chip-row">
          {(data.suggestedQuestions ?? []).map((q) => (
            <button key={q} type="button" className="chip" onClick={() => void ask(q)}>{q}</button>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (question.trim().length >= 3) void ask(question.trim());
          }}
        >
          <label className="field">
            <span className="label-text">Your question</span>
            <input value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={300} placeholder="e.g. Why did legal expenses increase this month?" />
          </label>
          <button type="submit" className="btn" disabled={asking || question.trim().length < 3}>{asking ? 'Calculating…' : 'Ask'}</button>
        </form>
        {answer && <AnswerCard answer={answer} />}
      </div>
    </div>
  );
}

function AnswerCard({ answer }: { answer: Record<string, unknown> }) {
  const figures = (answer.figures as Array<{ label: string; value: string }>) ?? [];
  const assumptions = (answer.assumptions as string[]) ?? [];
  const sources = (answer.sources as Array<{ label: string; report: string; page: number | null }>) ?? [];
  const followUps = (answer.followUps as string[]) ?? [];
  const scope = answer.scope as { community: string; reportedThrough: string } | undefined;
  return (
    <div className="card flat" style={{ marginTop: 12 }}>
      <p>{String(answer.answer)}</p>
      {figures.length > 0 && (
        <div className="stat-grid">
          {figures.map((f) => (
            <div className="stat" key={f.label}><span className="value" style={{ fontSize: 18 }}>{f.value}</span><span className="label">{f.label}</span></div>
          ))}
        </div>
      )}
      {assumptions.length > 0 && assumptions.map((a) => <p className="mut" key={a} style={{ margin: '4px 0' }}>{a}</p>)}
      {sources.length > 0 && (
        <p className="mut">
          Sources: {sources.map((s) => `${s.label} · ${s.report}${s.page ? ` · page ${s.page}` : ''}`).join('; ')}
        </p>
      )}
      {scope && <p className="mut">Scoped to {scope.community}, through {scope.reportedThrough}.</p>}
      {followUps.length > 0 && <p className="mut">You could also ask: {followUps.join(' · ')}</p>}
    </div>
  );
}
